import React from 'react';
import { AppState, Gift as GiftModel } from 'acscp/store';
import { Dispatch } from 'redux';
import { AmendBasket } from 'acscp/actions';
import { connect } from 'react-redux';
import style from './basketGiftQuantityModifier.module.scss';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    getQuantityOfGiftInBasket,
    getRemainingBalanceAfterSubmittingCurrentBasket
} from 'acscp/store/helpers/basketHelper';
import { TransitionGroup } from 'react-transition-group';
import { FadeSwitchTransition } from 'acscp/components/common/fadeSwitchTransition';

interface BasketGiftQuantityModifierDispatchProps {
    incrementQuantity: (giftId: string) => void;
    decrementQuantity: (giftId: string) => void;
}

interface BasketGiftQuantityModifierStateProps {
    canIncrement: boolean;
    canDecrement: boolean;
    currentQuantity: number;
}

interface BasketGiftQuantityModifierOwnProps {
    gift: GiftModel;
    counterOnly: boolean;
}

type BasketGiftQuantityModifierProps =
    BasketGiftQuantityModifierStateProps
    & BasketGiftQuantityModifierDispatchProps
    & BasketGiftQuantityModifierOwnProps;

export class BasketGiftQuantityModifierPresentational extends React.Component<BasketGiftQuantityModifierProps, {}> {
    private incrementIfPossible(): void {
        if (this.props.canIncrement) {
            this.props.incrementQuantity(this.props.gift.id);
        }
    }
    private decrementIfPossible(): void {
        if (this.props.canDecrement) {
            this.props.decrementQuantity(this.props.gift.id);
        }
    }

    render(): JSX.Element | null {
        const { counterOnly } = this.props;

        return (
            <div className={style.BasketGiftQuantityModifier}>
                {counterOnly
                    ? this.renderQuantityControls()
                    : (
                        <TransitionGroup>
                            {this.props.currentQuantity === 0
                                ? (
                                    <FadeSwitchTransition key={'addToBasket'}>
                                        {this.renderAddToBasketButton()}
                                    </FadeSwitchTransition>
                                ) : (
                                    <FadeSwitchTransition key={'modifyQuantity'}>
                                        <span>
                                            <span className={style.InBasketLabel}>
                                                <FormattedMessage
                                                    id={'components.loyalty.basketModifier.inBasket'}
                                                    defaultMessage={'In Basket:'}
                                                />
                                            </span>
                                            <span className={style.InlineQuantityControlsContainer}>
                                                {this.renderQuantityControls()}
                                            </span>
                                        </span>
                                    </FadeSwitchTransition>
                                )}
                        </TransitionGroup>
                    )}
            </div>
        );
    }

    private renderAddToBasketButton(): JSX.Element {
        const { canIncrement } = this.props;
        return (
            <button
                className={`${style.AddToBasket} ${canIncrement || style.Disabled}`}
                aria-disabled={!canIncrement}
                aria-label="Add item to the basket"
                onClick={() => this.incrementIfPossible()}
            >
                <FormattedMessage
                    id={'components.loyalty.basketModifier.addToBasket'}
                    defaultMessage={'ADD TO BASKET'}
                />
            </button>
        );
    }

    private renderQuantityControls(): JSX.Element {
        const { canIncrement, canDecrement, currentQuantity } = this.props;
        return (
            <span className={style.QuantityControls}>
                    <button
                        className={`${style.Minus} ${canDecrement || style.Disabled}`}
                        aria-disabled={!canDecrement}
                        aria-label="Remove one from the basket"
                        onClick={() => this.decrementIfPossible()}
                    />
                    <span className={style.Quantity}>
                        <FormattedNumber value={currentQuantity}/>
                    </span>
                    <button
                        className={`${style.Plus} ${canIncrement || style.Disabled}`}
                        aria-disabled={!canIncrement}
                        aria-label="Add another one to the basket"
                        onClick={() => this.incrementIfPossible()}
                    />
                </span>
        );
    }
}

function mapStateToProps(
    state: AppState,
    ownProps: BasketGiftQuantityModifierOwnProps
): BasketGiftQuantityModifierStateProps {
    const currentQuantity = getQuantityOfGiftInBasket(state.gifts, ownProps.gift);
    const numMoreCanAdd = Math.max(
        Math.floor(getRemainingBalanceAfterSubmittingCurrentBasket(state.gifts) / ownProps.gift.price),
        0);

    return {
        canIncrement: numMoreCanAdd > 0,
        canDecrement: currentQuantity > 0,
        currentQuantity,
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): BasketGiftQuantityModifierDispatchProps {
    return {
        incrementQuantity: (giftId: string) => dispatch(new AmendBasket(giftId, 1)),
        decrementQuantity: (giftId: string) => dispatch(new AmendBasket(giftId, -1)),
    };
}

export const BasketGiftQuantityModifier =
    connect(mapStateToProps, mapDispatchToProps)(BasketGiftQuantityModifierPresentational);
